import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeroSearch } from "@/components/HeroSearch";
import { ArrowUpRight, Smartphone, Flame, Radio } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { LocationDialog } from "@/components/LocationDialog";
import { InstallAppButton } from "@/components/InstallGuide";
import { MoodFinder } from "@/components/MoodFinder";
import { DealsCarousel } from "@/components/DealsCarousel";
import { LiveMenuBadge } from "@/components/LiveMenuBadge";
import { DispensaryCover } from "@/components/DispensaryCover";
import { ShopRating } from "@/components/ShopRating";
import { useShopRatings } from "@/hooks/useShopRatings";
import { useNativeShopRatings } from "@/hooks/useNativeShopRatings";
import { HeroStats } from "@/components/HeroStats";
import { HomeCategoryChips } from "@/components/HomeCategoryGrid";
import { EmailSubscribe } from "@/components/EmailSubscribe";
import { timeAgo } from "@/lib/time";

const HERO_BG =
  "https://images.unsplash.com/photo-1518465444133-93542d08fdd9?w=1920&q=80";

export default function HomePage() {
  const { location, ageVerified } = useApp();
  const [data, setData] = useState({ dispensaries: [], strains: [] });
  const [locOpen, setLocOpen] = useState(false);
  const [trulieveOnly, setTrulieveOnly] = useState(true);

  useEffect(() => {
    api.get("/featured").then((r) => setData(r.data));
    // Public app config drives header copy — see /api/public-config.
    // Default to Trulieve-only optimistically so the very first paint
    // matches what the demo actually shows (avoids a copy flicker if
    // the config request loses the race with the h1 render).
    api
      .get("/public-config")
      .then((r) => setTrulieveOnly(!!r.data?.trulieve_only))
      .catch(() => {}); // non-fatal — keep the optimistic default
  }, []);

  const featuredRatings = useShopRatings((data.dispensaries || []).map((d) => d.slug));
  const featuredNativeRatings = useNativeShopRatings((data.dispensaries || []).map((d) => d.slug));

  const stateCode = (location?.state || "").toUpperCase();
  const stateName = { PA: "Pennsylvania", CO: "Colorado", OR: "Oregon" }[stateCode] || "Pennsylvania";

  useEffect(() => {
    if (ageVerified && !location) {
      const t = setTimeout(() => setLocOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [location, ageVerified]);

  return (
    <main>
      {/* Hero — full-bleed bud macro */}
      <section className="relative overflow-hidden grain isolate">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.55) saturate(1.15) contrast(1.05)",
          }}
        />
        <div className="absolute inset-0 z-0 ember-gradient" />
        <div
          className="absolute inset-0 z-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,17,15,0.15) 0%, rgba(15,17,15,0.5) 60%, hsl(var(--background)) 100%)",
          }}
        />

        <div className="container-narrow relative z-10 pt-6 pb-16 sm:pt-10 sm:pb-24 text-center">
          <h1
            className="font-serif text-2xl sm:text-5xl lg:text-6xl leading-[1.15] sm:leading-[1.02] tracking-tight text-foreground bud-in bud-in-delay-1 whitespace-nowrap sm:whitespace-normal"
            data-testid="hero-headline"
          >
            One search.{" "}
            <span
              className="italic"
              style={{
                color: "#D4A268",
                textShadow: "0 2px 30px rgba(212,162,104,0.25)",
              }}
            >
              {trulieveOnly ? "Every Trulieve." : "Every dispensary."}
            </span>
          </h1>

          <div className="mt-4 max-w-2xl mx-auto bud-in bud-in-delay-2">
            <HomeCategoryChips />
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2 text-xs bud-in bud-in-delay-3">
            {["Blue Dream", "OG Kush", "Wedding Cake", "Gelato", "Jack Herer"].map(
              (s) => (
                <Link
                  key={s}
                  to={`/search?q=${encodeURIComponent(s)}`}
                  data-testid={`quick-search-${s.toLowerCase().replace(/ /g, "-")}`}
                  className="pill glass-light hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                >
                  {s}
                </Link>
              )
            )}
          </div>

          <p
            className="mt-6 max-w-md mx-auto text-sm text-foreground/70 leading-snug bud-in bud-in-delay-3"
            data-testid="hero-subhead"
          >
            {trulieveOnly ? (
              <>
                Find any strain at every Trulieve in {stateCode || "PA"} —
                live menus, closest store, best price. Because insurance
                doesn&apos;t cover this.
              </>
            ) : (
              <>
                Compare every {stateCode || "PA"} dispensary&apos;s live
                menu. Same strain, lowest price — because insurance
                doesn&apos;t cover this.
              </>
            )}
          </p>

          <HeroStats />
        </div>
      </section>

      {/* Mood / effect finder */}
      <MoodFinder />

      {/* Compete = save — patient-pressure banner
          Commented out per user request (Trulieve-only mode). Nothing
          deleted; re-enable by removing the surrounding JSX comment. */}
      {/*
      <section
        data-testid="compete-save-banner"
        className="container-narrow mt-16"
      >
        <div className="relative overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/60 via-card to-card p-7 sm:p-10">
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-start gap-6">
            <div className="flex-1">
              <div className="eyebrow text-emerald-300 mb-2">
                Why BudLink exists
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-3">
                When dispensaries compete,{" "}
                <span className="text-emerald-300">you save money.</span>
              </h2>
              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed max-w-2xl mb-3">
                Insurance doesn&apos;t cover medical cannabis. Every gram, every
                cart, every tincture comes out of your pocket — and the same
                strain can cost <span className="text-foreground font-medium">30–40% more</span> at the dispensary down the street than the one
                across town. That&apos;s real money to a patient on a fixed
                budget.
              </p>
              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed max-w-2xl">
                BudLink shows every {stateCode || "PA"} dispensary&apos;s live menu side-by-side
                so you can find the right product at the right price without
                driving from shop to shop or calling around. Don&apos;t see
                your favorite dispensary?{" "}
                <span className="text-foreground font-medium">
                  Ask them to get listed.
                </span>{" "}
                The more shops on BudLink, the more they compete for your
                business — and the more you save on the medicine you depend on.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link
                  to="/dispensaries"
                  data-testid="compete-cta-shops"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-500 text-emerald-950 text-sm font-semibold hover:bg-emerald-400"
                >
                  Compare nearby dispensaries
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a
                  href="mailto:?subject=Get%20listed%20on%20BudLink&body=Hi%2C%20I%27m%20a%20BudLink%20patient%20and%20I%27d%20love%20to%20see%20your%20live%20menu%20on%20BudLink%20so%20I%20can%20compare%20prices.%20You%20can%20get%20listed%20for%20free%20here%3A%20https%3A%2F%2Fbudlink.linklitepro.com%2Fonboarding"
                  data-testid="compete-cta-ask"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border bg-background/50 backdrop-blur text-sm hover:bg-card"
                >
                  Ask your shop to get listed
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Today's Deals */}
      <DealsCarousel />

      {/* Featured dispensaries */}
      <section className="container-narrow mt-4">
        <div className="flex items-end justify-between mb-7">
          <div>
            <div className="eyebrow mb-2 flex items-center gap-2">
              <Flame className="w-3.5 h-3.5 text-accent" strokeWidth={1.6} />{" "}
              Featured dispensaries
            </div>
            <h2 className="font-serif text-4xl">Picks across Pennsylvania</h2>
          </div>
          <Link
            to="/dispensaries"
            data-testid="home-see-all-dispensaries"
            className="text-sm text-foreground/70 hover:text-foreground flex items-center gap-1"
          >
            See all <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.dispensaries.map((d) => (
            <Link
              key={d.slug}
              to={`/dispensaries/${d.slug}`}
              data-testid={`home-disp-${d.slug}`}
              className="card-hover bg-card border border-border rounded-2xl overflow-hidden block"
            >
              <div className="aspect-[16/10] bg-secondary overflow-hidden relative">
                {d.cover_url ? (
                  <img
                    src={d.cover_url}
                    alt={d.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      // CDN hiccup or stale cover — swap to the SVG fallback
                      // so a featured card never renders a broken-image icon.
                      const wrap = e.currentTarget.parentElement;
                      e.currentTarget.style.display = "none";
                      const fb = wrap?.querySelector("[data-cover-fallback]");
                      if (fb) fb.style.display = "block";
                    }}
                  />
                ) : null}
                <div
                  data-cover-fallback
                  style={{
                    display: d.cover_url ? "none" : "block",
                    position: "absolute",
                    inset: 0,
                  }}
                >
                  <DispensaryCover
                    name={d.name}
                    slug={d.slug}
                    city={d.city}
                    posHint={d.pos_hint}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">
                      {d.city}, {d.state}
                    </div>
                    <div className="font-serif text-xl">{d.name}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {d.featured && (
                      <span className="pill pill-accent text-[10px]">Featured</span>
                    )}
                    {d.live_menu && <LiveMenuBadge size="xs" />}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">{d.hours}</p>
                <div className="flex gap-2 mt-3 flex-wrap items-center">
                  <ShopRating
                    slug={d.slug}
                    variant="native"
                    rating={featuredNativeRatings[d.slug]?.rating}
                    count={featuredNativeRatings[d.slug]?.rating_count}
                  />
                  <ShopRating
                    slug={d.slug}
                    variant="google"
                    rating={featuredRatings[d.slug]?.rating}
                    count={featuredRatings[d.slug]?.rating_count}
                  />
                  {d.pickup && <span className="pill text-xs">Pickup</span>}
                  {d.delivery && <span className="pill text-xs">Delivery</span>}
                  {d.menu_updated_at && (
                    <span
                      data-testid="menu-freshness"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-400 ml-auto"
                    >
                      <Radio className="w-2.5 h-2.5 animate-pulse" strokeWidth={2.4} />
                      {timeAgo(d.menu_updated_at)}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular strains */}
      <section className="container-narrow mt-24">
        <div className="flex items-end justify-between mb-7">
          <div>
            <div className="eyebrow mb-2">Popular strains</div>
            <h2 className="font-serif text-4xl">Browse the catalog</h2>
          </div>
          <Link
            to="/strains"
            data-testid="home-see-all-strains"
            className="text-sm text-foreground/70 hover:text-foreground flex items-center gap-1"
          >
            See all <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {data.strains.map((s) => (
            <Link
              key={s.slug}
              to={`/strains/${s.slug}`}
              data-testid={`home-strain-${s.slug}`}
              className="card-hover bg-card border border-border rounded-2xl overflow-hidden block"
            >
              <div className="aspect-square bg-secondary overflow-hidden relative">
                <img
                  src={s.image_url || "https://images.unsplash.com/photo-1603909223429-69bb7101f420?auto=format&fit=crop&w=900&q=80"}
                  alt={s.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1603909223429-69bb7101f420?auto=format&fit=crop&w=900&q=80";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-transparent to-transparent" />
              </div>
              <div className="p-4">
                <div className="eyebrow mb-1">{s.type}</div>
                <div className="font-serif text-lg">{s.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Sales alerts opt-in */}
      <EmailSubscribe variant="card" source="home" />

      {/* Install on phone */}
      <section className="container-narrow mt-24">
        <div className="rounded-3xl border border-border bg-card p-8 sm:p-12 grid md:grid-cols-[1fr_auto] items-center gap-6">
          <div className="flex items-start gap-5">
            <div className="hidden sm:flex w-14 h-14 rounded-2xl bg-primary text-primary-foreground items-center justify-center shrink-0">
              <Smartphone className="w-6 h-6" strokeWidth={1.6} />
            </div>
            <div>
              <div className="eyebrow mb-2">Save it to your phone</div>
              <h3 className="font-serif text-3xl">
                Bud Link, one tap from your home screen.
              </h3>
              <p className="text-sm text-foreground/70 mt-2 max-w-lg leading-relaxed">
                No App Store, no install file. Tap the button below and follow
                the quick guide for iPhone or Android — Bud Link will open
                full-screen like any other app.
              </p>
            </div>
          </div>
          <InstallAppButton testId="home-install-btn" label="Install Bud Link" />
        </div>
      </section>

      {/* CTA — commented out per user request (Trulieve-only mode).
          Nothing deleted; re-enable by removing the surrounding JSX
          comment. /pricing route still loads directly. */}
      {/*
      <section className="container-narrow mt-12">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-card to-secondary p-10 sm:p-14 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="max-w-lg">
            <div className="eyebrow text-accent mb-2">For dispensaries</div>
            <h3 className="font-serif text-3xl sm:text-4xl">
              Sell more. <span className="italic gold">Skip the gimmicks.</span>
            </h3>
            <p className="text-foreground/70 mt-3 text-sm leading-relaxed">
              Claim your listing, sync your menu, and reach customers actively
              searching. 30-day free trial. Cancel anytime.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="home-cta-pricing"
            className="h-12 px-7 inline-flex items-center rounded-full bg-primary text-primary-foreground text-sm hover:bg-primary/90"
          >
            See plans <ArrowUpRight className="w-4 h-4 ml-1.5" />
          </Link>
        </div>
      </section>
      */}
      <LocationDialog open={locOpen} onOpenChange={setLocOpen} />
    </main>
  );
}
